import React from 'react';
import { Icon } from '@/components/ui';
import { NavItem } from './navConfig';
import { openFeedback } from './feedback';

interface MoreSheetProps {
  open: boolean;
  items: NavItem[];
  activeTabId: string | null;
  onNavigate: (path: string) => void;
  onClose: () => void;
  onLogout: () => void;
  // Client-only "Give feedback" row (external Typeform). Shared sheet — staff
  // shell leaves this unset, the client shell passes true.
  showFeedback?: boolean;
  // Opens the Contact Support modal for an item with action 'contact-support'. Owned by the
  // host shell, not the sheet: selecting a row closes the sheet, which would unmount the modal.
  onContactSupport?: () => void;
}

export const MoreSheet = ({
  open,
  items,
  activeTabId,
  onNavigate,
  onClose,
  onLogout,
  showFeedback = false,
  onContactSupport,
}: MoreSheetProps) => {
  if (!open) return null;
  return (
    <div className="tw-absolute tw-inset-0 tw-z-40">
      <div
        onClick={onClose}
        className="tw-absolute tw-inset-0 tw-bg-[color:var(--backdrop)]"
      />
      <div
        className="tw-absolute tw-left-0 tw-right-0 tw-bottom-0 tw-bg-white tw-rounded-t-ds-xl tw-shadow-ds-e4"
        style={{
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="tw-flex tw-justify-center tw-pt-[10px] tw-pb-[4px]">
          <span className="tw-w-[40px] tw-h-[4px] tw-rounded-full tw-bg-ds-hairline" />
        </div>
        <div className="tw-px-[8px] tw-pt-[8px] tw-pb-[4px]">
          {showFeedback && (
            <button
              type="button"
              onClick={() => {
                onClose();
                openFeedback();
              }}
              className="tw-flex tw-items-center tw-gap-[14px] tw-w-full tw-px-[16px] tw-py-[14px] tw-border-0 tw-rounded-ds-m tw-cursor-pointer tw-text-left tw-font-poppins tw-text-[15px] tw-bg-ds-sky-mist tw-text-ds-primary tw-font-semibold"
            >
              <Icon name="megaphone" size={20} color="var(--twilight-sky)" />
              <span className="tw-flex-1">Give feedback</span>
              <Icon
                name="external-link"
                size={16}
                color="var(--twilight-sky)"
              />
            </button>
          )}
          {items.map((m) => {
            const on = activeTabId === m.id;
            return (
              <button
                key={m.id}
                onClick={() => {
                  if (m.action === 'contact-support') onContactSupport?.();
                  else onNavigate(m.path);
                  onClose();
                }}
                className={`tw-flex tw-items-center tw-gap-[14px] tw-w-full tw-px-[16px] tw-py-[14px] tw-border-0 tw-rounded-ds-m tw-cursor-pointer tw-text-left tw-font-poppins tw-text-[15px] ${
                  on
                    ? 'tw-bg-ds-sky-mist tw-text-ds-primary tw-font-semibold'
                    : 'tw-bg-transparent tw-text-ds-ink-soft tw-font-medium'
                }`}
              >
                <Icon
                  name={m.icon}
                  size={20}
                  color={on ? 'var(--twilight-sky)' : 'var(--ink-mute)'}
                />
                {m.label}
              </button>
            );
          })}
          <div className="tw-h-px tw-bg-ds-hairline tw-mx-[16px] tw-my-[6px]" />
          <button
            onClick={onLogout}
            className="tw-flex tw-items-center tw-gap-[14px] tw-w-full tw-px-[16px] tw-py-[14px] tw-border-0 tw-rounded-ds-m tw-cursor-pointer tw-text-left tw-font-poppins tw-text-[15px] tw-bg-transparent tw-text-ds-danger tw-font-medium"
          >
            <Icon name="log-out" size={20} color="var(--danger)" />
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};
